/**
 * AppContext - Application-wide state provider
 *
 * Provides authentication state and organizer data to all components.
 * Uses useAuth hook internally for auth logic.
 *
 * IMPORTANT: Do NOT use useAuth directly in components. Always use
 * useApp() from this context -- it provides a single shared auth instance.
 */
import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../hooks';

const AppContext = createContext();

export function AppProvider({ children }) {
  const auth = useAuth();

  // Provide both new auth properties and legacy aliases for compatibility
  const value = useMemo(() => ({
    // New auth hook values (preferred)
    ...auth,

    // Legacy aliases for backward compatibility
    currentOrganizer: auth.user,
    setCurrentOrganizer: auth.setUser,
    isOrganizer: auth.isAuthenticated,
    // isOnboardingComplete is the same in both
  }), [auth]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Hook to access app context.
 *
 * This is the correct way to access auth state in components.
 * Do NOT import useAuth directly from hooks -- it creates independent
 * state per component. Always use useApp() which shares a single
 * useAuth instance via context.
 */
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
